import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Check,
  ArrowUpRight,
  Loader2,
  ShieldCheck,
  Sparkles,
  Crown,
  Film,
  Megaphone,
  Handshake,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import AnimeVideoGallery from "@/components/AnimeVideoGallery";

const tiers = [
  {
    id: "artist_foundation",
    name: "Foundation",
    metalTier: "Gold",
    tagline: "Perfect for independent artists getting their business in order.",
    price: "$499",
    cadence: "",
    badge: "Step 1",
    cta: "Start Foundation",
    icon: ShieldCheck,
    bestFor: "New artists and artists with limited catalog.",
    features: [
      "Publishing affiliation setup",
      "Rights administration",
      "Metadata review",
      "Distribution setup / alignment",
      "Streaming royalty optimization",
      "Professional artist foundation",
    ],
  },
  {
    id: "artist_professional",
    name: "Professional",
    metalTier: "Platinum",
    tagline: "Everything in Foundation, plus the full infrastructure build-out.",
    price: "$999",
    cadence: "",
    badge: "Most chosen",
    highlight: true,
    cta: "Go Professional",
    icon: Sparkles,
    bestFor: "Artists with multiple releases already available on streaming platforms.",
    features: [
      "Everything in Foundation",
      "Complete artist infrastructure",
      "Dashboard integrations",
      "Catalog optimization",
      "Release strategy",
      "Monetization audit",
      "Priority support",
    ],
  },
  {
    id: "artist_legacy",
    name: "Legacy",
    metalTier: "Diamond",
    tagline: "Designed for established artists and catalogs.",
    price: "Starting at $1,999",
    cadence: "",
    badge: "White-glove",
    cta: "Build my Legacy",
    icon: Crown,
    bestFor: "Artists with years of releases, collaborations, and complex ownership structures.",
    features: [
      "Complete catalog migration",
      "Publishing overhaul",
      "Royalty recovery strategy",
      "Advanced rights administration",
      "Long-term infrastructure planning",
      "White-glove onboarding",
    ],
  },
];

const addons = [
  {
    id: "addon_ai_visuals",
    name: "Signature Visuals",
    price: "Starting at $299",
    tagline: "Music videos, visualizers, lyric videos, promotional clips, social content.",
    icon: Film,
    bullets: [
      "Signature music videos",
      "Visualizers",
      "Lyric videos",
      "Promotional clips",
      "Social content",
    ],
    note: "Pricing depends on scope and complexity.",
  },
  {
    id: "addon_artist_marketing",
    name: "Marketing Subscription",
    price: "$299",
    cadence: "/mo",
    tagline: "Monthly artist marketing engine — release strategy, social campaigns, content planning, brand positioning, audience growth.",
    icon: Megaphone,
    bullets: [
      "Monthly release strategy session",
      "Social media campaigns",
      "Ongoing content planning",
      "Brand positioning",
      "Audience growth strategy",
    ],
    note: "Recurring. Cancel anytime from your dashboard.",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);

  const startCheckout = async (planId) => {
    if (!user) {
      toast.message("Sign in to start checkout.");
      nav("/login", { state: { from: { pathname: "/pricing" } } });
      return;
    }
    if (user.role === "admin") {
      toast.message("Admin accounts can't purchase plans.");
      return;
    }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoadingPlan(null);
    }
  };

  return (
    <div className="bg-[#050505] text-white">
      <Seo
        title="Pricing — Gold, Platinum, Diamond consulting plans"
        description="Three-tier consulting plans for serious artists: Gold Foundation setup, Platinum Catalogue administration, and Diamond Empire white-glove management. Royalty recovery, publishing, distribution, sync."
        path="/pricing"
      />
      {/* Hero */}
      <section className="border-b border-white/10 py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="tag mb-6">// Artist Infrastructure Setup</div>
          <h1 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter max-w-5xl">
            Your music.
            <br />
            Your money.
            <br />
            <span className="text-[var(--primary)]">Your power.</span>
          </h1>
          <p className="mt-10 max-w-2xl text-zinc-300 text-lg leading-relaxed">
            Choose the tier that fits where you are. Transparent flat fees. Keep
            100% of your masters and publishing. Always.
          </p>
        </div>
      </section>

      {/* TIERS */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {tiers.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.id}
                  data-testid={`pricing-card-${t.id}`}
                  className={
                    "relative p-8 md:p-10 flex flex-col border " +
                    (t.highlight
                      ? "bg-[#0e0e0e] border-[var(--primary)]/60 glow-gold"
                      : "bg-[#0a0a0a] border-white/10")
                  }
                >
                  {t.badge && (
                    <div
                      className={
                        "absolute -top-3 left-8 text-[10px] tracking-widest font-bold px-3 py-1 uppercase " +
                        (t.highlight
                          ? "bg-[var(--primary)] text-black"
                          : "bg-[#0a0a0a] text-zinc-400 border border-white/20")
                      }
                    >
                      {t.badge}
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-6">
                    <div className="w-11 h-11 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)]">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                  </div>

                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    {t.name}
                    {t.metalTier && (
                      <span className="ml-2 text-[var(--primary)]">/ {t.metalTier}</span>
                    )}
                  </div>
                  <div className="font-heading font-black text-5xl tracking-tighter mb-2">
                    {t.price}
                    {t.cadence && (
                      <span className="text-base font-mono font-normal text-[var(--primary)] ml-1">
                        {t.cadence}
                      </span>
                    )}
                  </div>
                  <p className="text-zinc-300 text-sm mb-6 leading-relaxed">{t.tagline}</p>

                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Includes
                  </div>
                  <ul className="space-y-2.5 mb-8 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check size={16} className="text-[var(--primary)] mt-0.5 flex-shrink-0" strokeWidth={2} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <div className="border-t border-white/10 pt-4 mb-6">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      Best for
                    </div>
                    <p className="text-xs text-zinc-400 italic">{t.bestFor}</p>
                  </div>

                  <button
                    onClick={() => startCheckout(t.id)}
                    disabled={loadingPlan === t.id}
                    data-testid={`pricing-cta-${t.id}`}
                    className={t.highlight ? "btn-primary justify-center" : "btn-ghost justify-center"}
                  >
                    {loadingPlan === t.id ? (
                      <><Loader2 size={16} className="animate-spin" /> Redirecting…</>
                    ) : (
                      <>{t.cta} <ArrowUpRight size={14} /></>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI VISUAL GALLERY (conversion lever) */}
      <AnimeVideoGallery />

      {/* GROWTH ADD-ONS */}
      <section className="py-24 border-t border-white/10 bg-[#070707]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-7">
              <div className="tag mb-4">// Growth Add-Ons</div>
              <h2 className="font-heading font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
                Amplify
                <br />
                <span className="text-[var(--primary)]">your reach.</span>
              </h2>
            </div>
            <div className="md:col-span-5 flex items-end">
              <p className="text-zinc-400 text-base leading-relaxed">
                Stack these on top of any tier. Custom-scoped after a
                consultation — the starting price gets you in the door.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {addons.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.id}
                  data-testid={`pricing-card-${a.id}`}
                  className="bg-[#0a0a0a] p-8 md:p-10 flex flex-col card-hover"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-11 h-11 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)]">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-[10px] tracking-widest text-zinc-600">ADD-ON</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    {a.name}
                  </div>
                  <div className="font-heading font-black text-3xl tracking-tighter mb-2">
                    {a.price}
                    {a.cadence && (
                      <span className="text-zinc-500 text-lg font-normal tracking-normal">{a.cadence}</span>
                    )}
                  </div>
                  <p className="text-zinc-300 text-sm mb-6">{a.tagline}</p>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {a.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check size={16} className="text-[var(--primary)] mt-0.5 flex-shrink-0" strokeWidth={2} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-zinc-500 font-mono italic mb-6">{a.note}</p>
                  <button
                    onClick={() => startCheckout(a.id)}
                    disabled={loadingPlan === a.id}
                    data-testid={`pricing-cta-${a.id}`}
                    className="btn-ghost justify-center"
                  >
                    {loadingPlan === a.id ? (
                      <><Loader2 size={16} className="animate-spin" /> Redirecting…</>
                    ) : a.cadence ? (
                      <>Subscribe — {a.price}{a.cadence} <ArrowUpRight size={14} /></>
                    ) : (
                      <>Start at base price <ArrowUpRight size={14} /></>
                    )}
                  </button>
                </div>
              );
            })}

            {/* Revenue Partnership — application-only */}
            <div
              data-testid="pricing-card-revenue_partnership"
              className="bg-[#0a0a0a] p-8 md:p-10 flex flex-col card-hover md:col-span-3 border-t border-[var(--primary)]/40"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                <div className="md:col-span-7">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-11 h-11 border border-[var(--primary)]/40 bg-[var(--primary)]/5 flex items-center justify-center text-[var(--primary)]">
                      <Handshake size={20} strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-[10px] tracking-widest text-zinc-600 mt-3">PARTNERSHIP · APPLICATION REQUIRED</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                    Revenue Partnership
                  </div>
                  <div className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-2">
                    15% – 20%
                  </div>
                  <p className="text-[var(--primary)] text-sm font-mono mb-4">
                    Success-based structure aligned with artist growth.
                  </p>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    For select artists only. Zero upfront cost — we take a percentage on the royalties we recover and on the new revenue we unlock.
                  </p>
                </div>
                <div className="md:col-span-5">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
                    Available when
                  </div>
                  <ul className="space-y-2.5 mb-8">
                    {[
                      "Existing catalog demonstrates earning potential",
                      "Long-term partnership is appropriate",
                      "Revenue growth opportunities are identified",
                    ].map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-zinc-300">
                        <Check size={16} className="text-[var(--primary)] mt-0.5 flex-shrink-0" strokeWidth={2} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid="pricing-cta-revenue_partnership"
                    className="btn-primary w-full justify-center"
                  >
                    Apply for Partnership <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-10 text-xs text-zinc-600 font-mono text-center">
            Payments processed securely by Stripe. Test mode active — use card 4242 4242 4242 4242.
          </p>
        </div>
      </section>
    </div>
  );
}
